import './trail-map.css';

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l5 5L19 7" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Builds a gently winding SVG path through the node positions instead of
// straight segments, so the connector reads like a trail rather than a
// ruler line - each joint bows out to alternating sides.
function buildTrailPathD(nodes) {
  let d = `M ${nodes[0].x} ${nodes[0].y}`;
  for (let i = 1; i < nodes.length; i++) {
    const prev = nodes[i - 1];
    const curr = nodes[i];
    const midX = (prev.x + curr.x) / 2;
    const midY = (prev.y + curr.y) / 2;
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // Perpendicular to the segment, alternating sides each hop.
    const offset = (i % 2 === 0 ? 1 : -1) * 10;
    const ctrlX = midX + (-dy / len) * offset;
    const ctrlY = midY + (dx / len) * offset;
    d += ` Q ${ctrlX} ${ctrlY} ${curr.x} ${curr.y}`;
  }
  return d;
}

// Renders mission stops as pins placed directly on a background map image
// that fills the whole page (no scrolling). The active step's avatar sits
// on top and smoothly travels to its new spot whenever the active stop
// changes. Shared by the Mission Map and My Journey pages so both read as
// the same "game path" visual language.
// showPath draws a dashed connector + soft platform circles in CSS/SVG
// instead of relying on a background map image - use it on screens that
// don't have their own map artwork.
export default function TrailMap({
  nodes,
  activeId,
  avatarSrc,
  className = '',
  showPath = false,
  roadPath = false,
}) {
  const activeNode = nodes.find((node) => node.id === activeId);
  const pathD = showPath && nodes.length > 1 ? buildTrailPathD(nodes) : null;

  return (
    <div className={`trail-board ${className}`}>
      {pathD && (
        <svg className="trail-path-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          {roadPath ? (
            <>
              {/* Wide soft "road" band with a dashed centre line. */}
              <path
                d={pathD}
                fill="none"
                stroke="#cabfe6"
                strokeWidth="20"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={pathD}
                fill="none"
                stroke="#f1ecfa"
                strokeWidth="2.5"
                strokeDasharray="5 8"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </>
          ) : (
            <path
              d={pathD}
              fill="none"
              stroke="#c3b6ef"
              strokeWidth="3"
              strokeDasharray="2 4"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      )}

      {nodes.map((node) => (
        <div
          key={node.id}
          className="trail-node-wrap"
          style={{ left: `${node.x}%`, top: `${node.y}%` }}
        >
          <span className="trail-node-pin-stack">
            {showPath && <span className="trail-node-platform" />}
            <button
              type="button"
              className={
                'trail-node' +
                (node.state === 'active' ? ' trail-node-active' : '') +
                (node.state === 'locked' ? ' trail-node-locked' : '') +
                (node.state === 'done' ? ' trail-node-done' : '')
              }
              style={node.state !== 'locked' && node.color ? { background: node.color } : undefined}
              disabled={node.disabled}
              onClick={node.onClick}
              aria-label={node.ariaLabel || node.label}
            >
              {node.badge != null && <span className="trail-node-badge">{node.badge}</span>}
              {node.content}
            </button>
          </span>
          {node.label && node.state !== 'locked' && (
            <span className="trail-node-label">
              {node.state === 'done' && (
                <span className="trail-node-label-check">
                  <CheckIcon />
                </span>
              )}
              <span>{node.label}</span>
            </span>
          )}
        </div>
      ))}

      {activeNode && avatarSrc && (
        <div className="trail-avatar-wrap" style={{ left: `${activeNode.x}%`, top: `${activeNode.y}%` }}>
          <img src={avatarSrc} alt="" className="trail-node-avatar" />
        </div>
      )}
    </div>
  );
}
